import React, { useCallback, useEffect, useMemo, useState } from 'react';

import styled, { useTheme } from 'styled-components';

import { Box } from '@mui/material';
import {
  Button,
  FormControlLabel,
  Menu,
  Stack,
  SaveIcon,
  Switch,
  Tooltip,
  Typography,
} from 'components';
import { CloseCircle, More, Setting2 } from 'iconsax-react';

import { TableColumn } from './types';

import {
  DragDropContext,
  Draggable,
  DraggableProvided,
  Droppable,
  DroppableProvided,
} from 'third-party';
import { useTranslation } from 'third-party';

import { ICON_SIZE_MD } from 'constants/common';

const Wrapper = styled.div`
  padding: 0.5rem 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
`;

const Label = styled(Typography)`
  font-size: 1.6rem;
  font-weight: 400;
  margin-left: 1rem;
`;

type Props<ItemT> = {
  columns: TableColumn<ItemT>[];
  onColumnVisibilityChanged: (columnId: string, value: boolean) => void;
  onColumnOrderChanged?: (columns: TableColumn<ItemT>[]) => void;
  onSaveTableSettings?: (columns: TableColumn<ItemT>[]) => void;
  onResetTableSettings?: () => void;
  draggable?: boolean;
};

export const TableSettings = <ItemT,>({
  columns,
  onColumnVisibilityChanged,
  onSaveTableSettings,
  onResetTableSettings,
  onColumnOrderChanged,
  draggable = true,
}: Props<ItemT>) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const canBeHiddenColumns = useMemo(() => {
    return columns.filter(it => !it.cannotBeHidden);
  }, [columns]);

  const [popupAnchorEl, setPopupAnchorEl] = useState<null | Element>();
  const [draggableColumns, setDraggableColumns] = useState(canBeHiddenColumns);

  useEffect(() => {
    setDraggableColumns(canBeHiddenColumns);
  }, [canBeHiddenColumns]);

  const closePopup = useCallback(() => {
    setPopupAnchorEl(null);
  }, []);

  const onTriggerButtonClicked = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    setPopupAnchorEl(event.currentTarget);
  };

  const onDragEnd = (result: any) => {
    if (!draggable || !result.destination) return;

    const reorderedColumns = Array.from(draggableColumns);
    const [removed] = reorderedColumns.splice(result.source.index, 1);
    reorderedColumns.splice(result.destination.index, 0, removed);

    setDraggableColumns(reorderedColumns);
    if (onColumnOrderChanged) {
      onColumnOrderChanged(reorderedColumns);
    }
  };

  const handleColumnVisibilityChange = useCallback(
    (columnId: string, value: boolean) => {
      setDraggableColumns(prevColumns =>
        prevColumns.map(column =>
          column.dataId.toString() === columnId
            ? { ...column, hidden: !value }
            : column,
        ),
      );
      onColumnVisibilityChanged(columnId, value);
    },
    [onColumnVisibilityChanged],
  );

  return (
    <>
      <Tooltip title={t('table.settings')} arrow>
        <Button onClick={onTriggerButtonClicked}>
          <Setting2 variant="TwoTone" size="24" />
        </Button>
      </Tooltip>
      <Menu
        keepMounted
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        anchorEl={popupAnchorEl}
        open={!!popupAnchorEl}
        onClose={closePopup}
        sx={{
          marginTop: '2rem',
        }}
        MenuListProps={{
          'aria-labelledby': 'basic-button',
          sx: {},
        }}
      >
        <Wrapper>
          {(onSaveTableSettings || onResetTableSettings) && (
            <Stack direction="row" spacing={1}>
              {onSaveTableSettings && (
                <Tooltip title={t('table.saveTableState')} arrow>
                  <Button
                    onClick={() => {
                      onSaveTableSettings(
                        // @ts-ignore
                        draggableColumns.map(({ ref, formatter, ...rest }) => ({
                          ...rest,
                          order: draggableColumns.findIndex(
                            col => col.dataId === rest.dataId,
                          ),
                        })),
                      );
                      closePopup();
                    }}
                    startIcon={<SaveIcon size={ICON_SIZE_MD} />}
                    sx={{ justifyContent: 'center' }}
                  />
                </Tooltip>
              )}
              {onResetTableSettings && (
                <Tooltip title={t('table.clearTableFilterState')} arrow>
                  <Button
                    onClick={() => {
                      onResetTableSettings();
                      closePopup();
                    }}
                    startIcon={<CloseCircle variant="TwoTone" size="24" />}
                    sx={{ justifyContent: 'center' }}
                  />
                </Tooltip>
              )}
            </Stack>
          )}
          <Typography
            variant="h3"
            textTransform="uppercase"
            color={theme.custom.palette.subTittle}
          >
            {t('table.columnVisibility')}
          </Typography>
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="columns">
              {(provided: DroppableProvided) => (
                <div
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  style={{ userSelect: 'none' }}
                >
                  {draggableColumns.map((it, index) => (
                    <Draggable
                      key={it.dataId.toString()}
                      draggableId={it.dataId.toString()}
                      index={index}
                      isDragDisabled={!draggable}
                    >
                      {(provided: DraggableProvided) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...(draggable ? provided.dragHandleProps : {})}
                        >
                          <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                          >
                            <FormControlLabel
                              control={
                                <Switch
                                  size="small"
                                  checked={!it.hidden}
                                  onChange={event =>
                                    handleColumnVisibilityChange(
                                      it.dataId.toString(),
                                      event.target.checked,
                                    )
                                  }
                                />
                              }
                              label={
                                <Label>
                                  {
                                    //@ts-ignore
                                    t(it.label)
                                  }
                                </Label>
                              }
                            />
                            {draggable && (
                              <Box
                                {...provided.dragHandleProps}
                                sx={{ cursor: 'grab', fontSize: '1rem' }}
                              >
                                <More variant="TwoTone" size="20" />
                              </Box>
                            )}
                          </Stack>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </Wrapper>
      </Menu>
    </>
  );
};
